import "./App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./lib/auth";
import { GlobalFiltersProvider } from "./lib/globalFilters";
import { ThemeProvider } from "./lib/theme";
import { Toaster } from "sonner";
import LoadingScreen from "./components/LoadingScreen";
import ErrorBoundary from "./components/ErrorBoundary";
import Login from "./pages/Login";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import AppLauncher from "./pages/AppLauncher";
import OrdenesConsolidada from "./pages/OrdenesConsolidada";
import OrdenDetalle from "./pages/OrdenDetalle";
import OrdenNueva from "./pages/OrdenNueva";
import Manual from "./pages/Manual";
import FlujosDecision from "./pages/FlujosDecision";
import Documentacion from "./pages/Documentacion";
import FlujoSistema from "./pages/FlujoSistema";
import Perfil from "./pages/Perfil";
import AdminUsuarios from "./pages/AdminUsuarios";
import AdminRoles from "./pages/AdminRoles";
import GuiaRoles from "./pages/GuiaRoles";
import CentroDocumentacion from "./pages/CentroDocumentacion";
import AuditLog from "./pages/AuditLog";
import Notificaciones from "./pages/Notificaciones";
import TiposSolucion from "./pages/TiposSolucion";
import DbExplorer from "./pages/DbExplorer";
import AdminDbStats from "./pages/AdminDbStats";
import AdminA1Audit from "./pages/AdminA1Audit";
import AdminA1Settings from "./pages/AdminA1Settings";
import AdminOdooLiveMonitor from "./pages/AdminOdooLiveMonitor";
import MonitoringCenter from "./pages/MonitoringCenter";
import CalendarioTecnicos from "./pages/CalendarioTecnicos";
import GestionTurnos from "./pages/GestionTurnos";
import MapaOTsDia from "./pages/MapaOTsDia";
import WarRoom from "./pages/WarRoom";
import WarRoomCC from "./pages/WarRoomCC";   // v95.0.0 · Centro de control
import WarRoomBoard from "./pages/WarRoomBoard";
import CategoriasMateriales from "./pages/CategoriasMateriales";
import OdooExplorer from "./pages/OdooExplorer";
import OTSmartInbox from "./pages/OTSmartInbox";
import FlowMonitor from "./pages/FlowMonitor";
import OTSmartDetail from "./pages/OTSmartDetail";
import InventarioMaster from "./pages/InventarioMaster";
import KardexInventario from "./pages/KardexInventario";   // v101.120.0
import ReporteComercial from "./pages/ReporteComercial";   // v101.123.0
import ReporteFiscalizacionIA from "./pages/ReporteFiscalizacionIA";   // v101.255.0
import MetricasLeadTime from "./pages/MetricasLeadTime";   // v101.179.0
import KPISistema from "./pages/KPISistema";   // v101.263.0
import Prefacturacion from "./pages/Prefacturacion";   // v101.193.0
import AdminHorarios from "./pages/AdminHorarios";
import AdminZonas from "./pages/AdminZonas";
import AdminEventosMasivos from "./pages/AdminEventosMasivos";
import AdminColaA6 from "./pages/AdminColaA6";
import AdminRutasA4 from "./pages/AdminRutasA4";
import AdminCalamidadesA5 from "./pages/AdminCalamidadesA5";
import AdminA2DryRun from "./pages/AdminA2DryRun";
import AdminReglasAgentes from "./pages/AdminReglasAgentes";
import AdminBrainConfig from "./pages/AdminBrainConfig";
import FiscalizacionCenter from "./pages/FiscalizacionCenter";
import AdminA2Bitacora from "./pages/AdminA2Bitacora";
import AdminA2ManualRescue from "./pages/AdminA2ManualRescue";
import AdminWhatsAppTemplates from "./pages/AdminWhatsAppTemplates";
import AdminWhatsAppConexion from "./pages/AdminWhatsAppConexion";   // v101.180.0
import FibiConversaciones from "./pages/FibiConversaciones";   // v101.196.0
import AdminA2Negotiations from "./pages/AdminA2Negotiations";
import ManualInteligencia from "./pages/ManualInteligencia";
import AdminMapeoBodegas from "./pages/AdminMapeoBodegas";
import AdminMotivosCierre from "./pages/AdminMotivosCierre";   // v101.101.0
import AdminValidaciones from "./pages/AdminValidaciones";   // v101.102.0
import AdminFormulariosFiscalizacion from "./pages/AdminFormulariosFiscalizacion";
import AdminRecetasV31 from "./pages/AdminRecetasV31";
import FiscalizacionOT from "./pages/FiscalizacionOT";
import AdminMapeoStages from "./pages/AdminMapeoStages";
import AdminProvisioningAudit from "./pages/AdminProvisioningAudit";
import TechOrderTimeline from "./pages/TechOrderTimeline";
import TechWorkCenter from "./pages/TechWorkCenter";
import KPITecnicos from "./pages/KPITecnicos";
import SystemTriggers from "./pages/SystemTriggers";
import AdminTrazabilidad from "./pages/AdminTrazabilidad";
import AgendamientoSucursales from "./pages/AgendamientoSucursales";   // v101.144.0
import SincronizacionOdoo from "./pages/SincronizacionOdoo";   // v101.162.0
import AmbienteRise from "./pages/AmbienteRise";   // v101.147.0
import RiseBanner from "./components/RiseBanner";   // v101.159.0 · identidad RISE en toda la web
import InfraRedRelleno from "./pages/InfraRedRelleno"; // v101.45.1
// v101.24.0 · Fibi · Ayuda IA (página + botón flotante)
import AyudaIA from "./pages/AyudaIA";
import FibiWidget from "./components/FibiWidget";
// v101.27.0 · «Entrar como» (barra fija mientras el super_admin ve como otro usuario)
import ImpersonationBanner from "./components/ImpersonationBanner";
import NotFound from "./pages/NotFound";

function ProtectedRoute({ children, allow, perm }) {
  const { user, loading, hasPerm } = useAuth();
  if (loading) return <LoadingScreen message="Verificando sesión..." />;
  if (!user) return <Navigate to="/login" replace />;
  // v3.6.3 · Bypass total super_admin
  if (user.role === "super_admin") return children;
  // Sin allow ni perm → sólo requiere autenticación
  if (!allow && !perm) return children;
  // Acceso concedido si el rol está en la whitelist O el usuario tiene el permiso.
  // Permite gestión dinámica: al asignar el permiso al rol, el módulo se habilita
  // sin necesidad de listar el rol explícitamente aquí.
  const roleOk = allow ? allow.includes(user.role) : false;
  const permOk = perm ? hasPerm(perm) : false;
  if (roleOk || permOk) return children;
  return <Navigate to="/apps" replace />;
}

// Solo super_admin (legacy admin) ve Flujos/Documentación/Manual
const SUPER_ADMIN = ["admin", "super_admin"];

function App() {
  return (
    <ErrorBoundary>
      <ThemeProvider>
        <AuthProvider>
          <GlobalFiltersProvider>
          <BrowserRouter>
            <Toaster position="top-right" richColors theme="light" gap={10} offset={18} visibleToasts={4} />
            <RiseBanner />
        <Routes>
          {/* Públicas */}
          <Route path="/login" element={<Login />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />

          {/* Home = App Launcher (estilo Odoo) */}
          <Route path="/" element={<Navigate to="/apps" replace />} />
          <Route path="/apps" element={<ProtectedRoute><AppLauncher /></ProtectedRoute>} />
          <Route path="/dashboard" element={<Navigate to="/apps" replace />} />

          {/* Hito 1 — Módulos productivos */}
          <Route path="/ordenes" element={<ProtectedRoute><OrdenesConsolidada /></ProtectedRoute>} />
          {/* Legacy paths → redirect a bandeja Smart única */}
          <Route path="/ordenes/kanban" element={<Navigate to="/ordenes" replace />} />
          <Route path="/ordenes/lista" element={<Navigate to="/ordenes" replace />} />
          <Route path="/ordenes/nueva" element={<ProtectedRoute allow={["admin", "super_admin", "admin_zone", "supervisor", "agendador", "scheduler", "soporte_tecnico"]} perm="M1.create"><OrdenNueva /></ProtectedRoute>} />
          <Route path="/ordenes/:id" element={<ProtectedRoute><OrdenDetalle /></ProtectedRoute>} />
          <Route path="/perfil" element={<ProtectedRoute><Perfil /></ProtectedRoute>} />

          {/* Administración (M17) */}
          <Route path="/admin/usuarios" element={<ProtectedRoute allow={["admin", "super_admin", "admin_zone"]} perm="M17.users.view"><AdminUsuarios /></ProtectedRoute>} />
          <Route path="/admin/roles" element={<ProtectedRoute allow={["admin", "super_admin", "admin_zone"]} perm="M17.roles.view"><AdminRoles /></ProtectedRoute>} />
          <Route path="/admin/auditoria" element={<ProtectedRoute allow={["admin", "super_admin", "admin_zone", "auditor"]} perm="M17.audit.view"><AuditLog /></ProtectedRoute>} />
          <Route path="/admin/notificaciones" element={<ProtectedRoute allow={["admin", "super_admin", "admin_zone"]} perm="M17.notifications.view"><Notificaciones /></ProtectedRoute>} />
          <Route path="/admin/guia-roles" element={<ProtectedRoute allow={["admin", "super_admin", "admin_zone"]} perm="M17.roles.view"><GuiaRoles /></ProtectedRoute>} />

          {/* Hito 2 · Inventario Maestro (unificado desde Odoo · legacy /inventario redirige) */}
          <Route path="/inventario" element={<Navigate to="/inventario-master" replace />} />
          <Route path="/infraestructura" element={<Navigate to="/ordenes" replace />} />

          {/* Hito 2 · Tipos de Solución */}
          <Route path="/tipos-solucion" element={<ProtectedRoute><TiposSolucion /></ProtectedRoute>} />

          {/* Documentación corporativa (solo Super Admin) */}
          <Route path="/flujo-sistema" element={<ProtectedRoute allow={SUPER_ADMIN}><FlujoSistema /></ProtectedRoute>} />
          <Route path="/flujos" element={<ProtectedRoute allow={SUPER_ADMIN}><FlujosDecision /></ProtectedRoute>} />
          <Route path="/documentacion" element={<ProtectedRoute allow={SUPER_ADMIN}><Documentacion /></ProtectedRoute>} />
          <Route path="/manual" element={<ProtectedRoute allow={SUPER_ADMIN}><Manual /></ProtectedRoute>} />
          <Route path="/explorador-db" element={<ProtectedRoute allow={["admin", "super_admin", "finance"]}><DbExplorer /></ProtectedRoute>} />
          <Route path="/admin/db-stats" element={<ProtectedRoute allow={["admin", "super_admin"]}><AdminDbStats /></ProtectedRoute>} />
          <Route path="/admin/a1-audit" element={<ProtectedRoute allow={["admin", "super_admin", "planner", "pmo"]}><AdminA1Audit /></ProtectedRoute>} />
          <Route path="/admin/a1-settings" element={<ProtectedRoute allow={["admin", "super_admin"]}><AdminA1Settings /></ProtectedRoute>} />
          <Route path="/admin/odoo-live" element={<ProtectedRoute allow={["admin", "super_admin"]}><AdminOdooLiveMonitor /></ProtectedRoute>} />
          {/* Legacy /admin/sucursales redirige al nuevo Mapa de Zonas unificado */}
          <Route path="/admin/sucursales" element={<Navigate to="/admin/zonas" replace />} />

          {/* Central de Monitoreo (M25 · v1.7.8) */}
          <Route path="/monitoreo" element={<ProtectedRoute perm="M25.monitoring.view"><MonitoringCenter /></ProtectedRoute>} />

          {/* v2.9.8 · Flow Monitor · trazabilidad end-to-end de una OT */}
          <Route path="/gestion/flow-monitor" element={<ProtectedRoute perm="M25.monitoring.view"><FlowMonitor /></ProtectedRoute>} />

          {/* v3.5.0 · War Room Unificado (reemplaza MapaGIS + Calendario + Mapa OTs día) */}
          <Route path="/warroom" element={<ProtectedRoute perm="M1.view"><WarRoomCC /></ProtectedRoute>} />
          <Route path="/warroom/anterior" element={<ProtectedRoute perm="M1.view"><WarRoom /></ProtectedRoute>} />
          <Route path="/warroom/board" element={<ProtectedRoute perm="M1.view"><WarRoomBoard /></ProtectedRoute>} />

          {/* M6 · Calendario Time-Slot por técnico → redirect a War Room v3.5.0 */}
          <Route path="/calendario-tecnicos" element={<ProtectedRoute perm="M1.view"><CalendarioTecnicos /></ProtectedRoute>} />

          {/* M5 · Mapa GPS de OTs del día → redirect a War Room v3.5.0 */}
          <Route path="/mapa-ots-dia" element={<Navigate to="/warroom" replace />} />

          {/* v3.5.0 · Legacy calendario/mapa como rutas escondidas (referencia) */}
          <Route path="/legacy/calendario-tecnicos" element={<ProtectedRoute perm="M1.view"><CalendarioTecnicos /></ProtectedRoute>} />
          <Route path="/turnos" element={<ProtectedRoute perm="M_SHIFTS.view"><GestionTurnos /></ProtectedRoute>} />
          <Route path="/legacy/mapa-ots-dia" element={<ProtectedRoute perm="M1.view"><MapaOTsDia /></ProtectedRoute>} />

          {/* M2 · Categorías de Materiales (QA 23-07) */}
          <Route path="/categorias-materiales" element={<ProtectedRoute perm="M6.inventory.view"><CategoriasMateriales /></ProtectedRoute>} />

          {/* v1.9.9 · Odoo Explorer (skeleton integración solo lectura) */}
          <Route path="/odoo" element={<ProtectedRoute roles={["super_admin","admin_zone","admin","soporte_tecnico"]}><OdooExplorer /></ProtectedRoute>} />

          {/* v2.0.0 · OT SMART (bandeja Odoo enriquecida) */}
          <Route path="/ot-smart" element={<Navigate to="/ordenes?tab=smart" replace />} />
          <Route path="/ot-smart/:odooTaskId" element={<ProtectedRoute perm="M1.view"><OTSmartDetail /></ProtectedRoute>} />

          {/* v2.4.0 · Inventario Maestro (Odoo stock_quant + products + locations + lots) */}
          <Route path="/inventario-master" element={<ProtectedRoute perm="M6.inventory.view"><InventarioMaster /></ProtectedRoute>} />
          <Route path="/kardex" element={<ProtectedRoute perm="M6.inventory.view"><KardexInventario /></ProtectedRoute>} />

          {/* v3.0.0 · Horarios laborales por técnico (día × turnos con descansos) */}
          <Route path="/admin/horarios" element={<ProtectedRoute perm="M_SHIFTS.view"><AdminHorarios /></ProtectedRoute>} />

          {/* v3.2.0 · Editor visual de zonas de cobertura (Leaflet + polígonos) */}
          <Route path="/admin/zonas" element={<ProtectedRoute allow={["admin", "super_admin"]} perm="M19.zones.view"><AdminZonas /></ProtectedRoute>} />

          {/* v3.3.0 · A3 · Bloqueo por eventos masivos */}
          <Route path="/admin/eventos-masivos" element={<ProtectedRoute allow={["admin", "super_admin", "admin_zone", "supervisor", "agendador"]}><AdminEventosMasivos /></ProtectedRoute>} />

          {/* v3.4.0 · A6 · Cola priorizada */}
          <Route path="/admin/cola-a6" element={<ProtectedRoute allow={["admin", "super_admin", "admin_zone", "supervisor"]}><AdminColaA6 /></ProtectedRoute>} />

          {/* v3.5.0 · A4 · Enrutamiento GPS diario */}
          <Route path="/admin/rutas-a4" element={<ProtectedRoute allow={["admin", "super_admin", "admin_zone", "supervisor"]}><AdminRutasA4 /></ProtectedRoute>} />

          {/* v3.6.0 · A5 · Calamidades + reasignación dinámica */}
          <Route path="/admin/calamidades-a5" element={<ProtectedRoute allow={["admin", "super_admin", "admin_zone", "supervisor", "agendador"]}><AdminCalamidadesA5 /></ProtectedRoute>} />

          {/* v3.7.0 · A2 · Motor orquestado (dry-run) */}
          <Route path="/admin/a2-dryrun" element={<ProtectedRoute allow={["admin", "super_admin", "admin_zone", "supervisor"]}><AdminA2DryRun /></ProtectedRoute>} />
          <Route path="/admin/a2-bitacora" element={<ProtectedRoute perm="M32.a2.view"><AdminA2Bitacora /></ProtectedRoute>} />
          <Route path="/admin/a2-rescate" element={<ProtectedRoute perm="M32.a2.view"><AdminA2ManualRescue /></ProtectedRoute>} />
          <Route path="/admin/reglas-agentes" element={<ProtectedRoute allow={["admin", "super_admin", "admin_zone", "supervisor"]}><AdminReglasAgentes /></ProtectedRoute>} />
          {/* v3.8.0 · Panel de Cerebros configurables · super_admin siempre pasa (bypass en ProtectedRoute) */}
          <Route path="/admin/brain-config" element={<ProtectedRoute allow={["admin", "super_admin", "admin_zone", "supervisor"]}><AdminBrainConfig /></ProtectedRoute>} />
          <Route path="/fiscalizacion" element={<ProtectedRoute allow={["admin", "super_admin", "admin_zone", "fiscalizer"]}><FiscalizacionCenter /></ProtectedRoute>} />
          {/* v3.9.1 · Aliases legacy → Centro Maestro de Fiscalización */}
          <Route path="/admin/fisc-matrix" element={<ProtectedRoute allow={["admin", "super_admin", "admin_zone", "fiscalizer"]}><FiscalizacionCenter /></ProtectedRoute>} />
          <Route path="/admin/fiscalizacion" element={<ProtectedRoute allow={["admin", "super_admin", "admin_zone", "fiscalizer"]}><FiscalizacionCenter /></ProtectedRoute>} />
          <Route path="/admin/whatsapp-templates" element={<ProtectedRoute allow={["admin", "super_admin"]}><AdminWhatsAppTemplates /></ProtectedRoute>} />
          <Route path="/admin/whatsapp-conexion" element={<ProtectedRoute allow={["admin", "super_admin"]}><AdminWhatsAppConexion /></ProtectedRoute>} />
          <Route path="/whatsapp/conversaciones" element={<ProtectedRoute allow={["admin", "super_admin", "admin_zone", "supervisor", "agendador", "scheduler", "atc"]}><FibiConversaciones /></ProtectedRoute>} />
          <Route path="/admin/a2-negotiations" element={<ProtectedRoute allow={["admin", "super_admin", "admin_zone", "supervisor"]}><AdminA2Negotiations /></ProtectedRoute>} />
          <Route path="/manual-inteligencia" element={<ProtectedRoute><ManualInteligencia /></ProtectedRoute>} />
          <Route path="/centro-documentacion" element={<ProtectedRoute><CentroDocumentacion /></ProtectedRoute>} />
          <Route path="/admin/validaciones" element={<ProtectedRoute allow={["admin","super_admin","admin_zone","supervisor"]}><AdminValidaciones /></ProtectedRoute>} />
          <Route path="/reporte-comercial" element={<ProtectedRoute allow={["admin","super_admin","admin_zone","supervisor"]}><ReporteComercial /></ProtectedRoute>} />
          <Route path="/reportes/fiscalizacion-ia" element={<ProtectedRoute allow={["admin","super_admin","admin_zone","supervisor","fiscalizer","finance"]}><ReporteFiscalizacionIA /></ProtectedRoute>} />
          <Route path="/prefacturacion" element={<ProtectedRoute allow={["admin","super_admin","fiscalizer","finance","admin_zone"]}><Prefacturacion /></ProtectedRoute>} />
          <Route path="/metricas/lead-time" element={<ProtectedRoute allow={["admin","super_admin","admin_zone","supervisor"]}><MetricasLeadTime /></ProtectedRoute>} />
          <Route path="/metricas/kpi" element={<ProtectedRoute allow={["admin","super_admin","admin_zone","supervisor","finance"]}><KPISistema /></ProtectedRoute>} />
          <Route path="/metricas/kpi/:clave" element={<ProtectedRoute allow={["admin","super_admin","admin_zone","supervisor","finance"]}><KPISistema /></ProtectedRoute>} />
          <Route path="/admin/motivos-cierre" element={<ProtectedRoute allow={["admin","super_admin"]}><AdminMotivosCierre /></ProtectedRoute>} />
          <Route path="/admin/mapeo-bodegas" element={<ProtectedRoute allow={["admin","super_admin","admin_zone"]} perm="M8.warehouses.view"><AdminMapeoBodegas /></ProtectedRoute>} />
          <Route path="/admin/formularios-fiscalizacion" element={<ProtectedRoute allow={["admin","super_admin","admin_zone","fiscalizer"]} perm="M9.forms.view"><AdminFormulariosFiscalizacion /></ProtectedRoute>} />
          {/* v3.1.0 · Recetas por Categoría + Fiscalización dinámica */}
          <Route path="/admin/recetas-v31" element={<ProtectedRoute allow={["admin","super_admin"]}><AdminRecetasV31 /></ProtectedRoute>} />
          <Route path="/fiscalizacion/:otId" element={<ProtectedRoute><FiscalizacionOT /></ProtectedRoute>} />
          <Route path="/admin/mapeo-stages" element={<ProtectedRoute allow={["admin","super_admin"]}><AdminMapeoStages /></ProtectedRoute>} />
          <Route path="/admin/provisioning-audit" element={<ProtectedRoute allow={["admin","super_admin","admin_zone","supervisor"]}><AdminProvisioningAudit /></ProtectedRoute>} />

          {/* v4.0.0 · Flujo Técnico Móvil (Culebra Timeline + Centro de Trabajo) */}
          <Route path="/tecnico/mis-ordenes" element={<ProtectedRoute><TechOrderTimeline /></ProtectedRoute>} />
          <Route path="/tecnico/orden/:orderId/centro-trabajo" element={<ProtectedRoute><TechWorkCenter /></ProtectedRoute>} />

          {/* KPI Técnicos — Capacidad por sucursal y zona */}
          <Route path="/kpi-tecnicos" element={<ProtectedRoute allow={["admin", "super_admin", "admin_zone", "supervisor", "agendador"]}><KPITecnicos /></ProtectedRoute>} />

          {/* v81.0.0 · Gestor de Triggers (Integraciones) */}
          <Route path="/integraciones/triggers" element={<ProtectedRoute perm="M40.triggers.view"><SystemTriggers /></ProtectedRoute>} />
          {/* v101.9.0 · Trazabilidad: logs y payloads de la app móvil y Odoo */}
          <Route path="/integraciones/sincronizacion-odoo" element={<ProtectedRoute allow={["admin", "super_admin", "admin_zone", "supervisor", "agendador", "scheduler"]}><SincronizacionOdoo /></ProtectedRoute>} />   {/* v101.162.0 */}
          <Route path="/integraciones/agendamiento-sucursales" element={<ProtectedRoute allow={["admin", "super_admin", "admin_zone", "supervisor", "agendador", "scheduler"]}><AgendamientoSucursales /></ProtectedRoute>} />   {/* v101.144.0 */}
          <Route path="/integraciones/ambiente-rise" element={<ProtectedRoute allow={["admin", "super_admin"]}><AmbienteRise /></ProtectedRoute>} />   {/* v101.147.0 */}
          <Route path="/integraciones/trazabilidad" element={<ProtectedRoute allow={["admin", "super_admin"]}><AdminTrazabilidad /></ProtectedRoute>} />
          <Route path="/integraciones/infraestructura-red" element={<ProtectedRoute allow={["super_admin"]}><InfraRedRelleno /></ProtectedRoute>} />

          {/* v101.24.0 · Fibi · Ayuda IA: todos los usuarios con sesión */}
          <Route path="/ayuda" element={<ProtectedRoute><AyudaIA /></ProtectedRoute>} />

          {/* 404 catch-all */}
          <Route path="*" element={<NotFound />} />
        </Routes>
        {/* v101.24.0 · Botón flotante «Fibi» (solo con sesión iniciada) */}
        <FibiWidget />
        <ImpersonationBanner />
        </BrowserRouter>
        </GlobalFiltersProvider>
      </AuthProvider>
     </ThemeProvider>
    </ErrorBoundary>
  );
}

export default App;
